'use client';

import posthog from 'posthog-js';
import { isMobile } from 'react-device-detect';
import type { BrowserFlags } from './flags.server';

let posthogClient: typeof posthog | null = null;

const initPosthog = () => {
  if (typeof window === 'undefined')
    throw new Error('PostHog client can only be initialized on the client');

  posthogClient = posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY || '', {
    api_host: `${window.location.origin}/ingest`,
    person_profiles: 'identified_only',
    capture_pageview: false,
    capture_pageleave: true,
    autocapture: false,
    session_recording: {
      maskAllInputs: true,
      maskInputOptions: {},

      recordCrossOriginIframes: false,
    },
    disable_session_recording: true,
    opt_out_capturing_by_default: false,
    advanced_disable_decide: false,
    loaded: posthog => {
      if (process.env.NODE_ENV === 'development') {
        console.log('PostHog loaded - user identification enabled');
      }
    },
  });
  return posthogClient;
};

let serverFlagsFor: string | null = null;

/**
 * Hand PostHog the flags the server read for this person with the page (flags.server
 * browserFlagsFor). They go in as a partial answer does, over what PostHog has, and a flag call
 * that later fails (a blocker: Dia, Brave, uBlock) leaves them in place.
 */
export const bootstrapFlags = (authUserId: string, flags: BrowserFlags) => {
  const client = getPosthogClient();
  if (!client) return;
  serverFlagsFor = authUserId;
  client.featureFlags.receivedFeatureFlags({ ...flags, errorsWhileComputingFlags: true });
};

/** Whether PostHog holds flags the server read for this person (bootstrapFlags). */
export const hasServerFlagsFor = (authUserId: string) => serverFlagsFor === authUserId;

export const getPosthogClient = () => {
  if (typeof window === 'undefined') return null;
  if (posthogClient) return posthogClient;
  return initPosthog();
};

/**
 * Start PostHog session replay, but never on mobile.
 *
 * Mobile replay is skipped deliberately: recording is heavy on constrained
 * devices. Idempotent — safe to call at any moment we want to be certain a
 * replay exists (e.g. a feature interaction we want to watch back).
 *
 * Returns true if recording was started, so callers can log accurately.
 */
export const startSessionReplayUnlessMobile = (): boolean => {
  const client = getPosthogClient();
  if (!client || isMobile) return false;
  client.startSessionRecording();
  return true;
};
